/**
 * Detector de corrupção de `String.prototype.toString` / `Number.prototype.toString`.
 *
 * Contexto (2026-09-23): um usuário teve a tela do deal derrubada por
 * "Number.prototype.toString requires that 'this' be a Number" — algo no navegador
 * trocou o `toString` de String pelo de Number. Não reproduziu em página recém-
 * carregada, e nada no nosso código ou no Sonax faz isso; suspeita de extensão ou
 * estado de aba antiga.
 *
 * Este guard NÃO bloqueia nem restaura nada — só registra no console quem trocou
 * (stack, quando a troca é por atribuição) ou quando a troca foi percebida (polling,
 * quando é por `defineProperty`). Peça ao usuário o console filtrado por
 * `[PrototypeGuard]`.
 */

const TAG = "[PrototypeGuard]";
const POLL_MS = 2000;

type Target = { name: string; proto: object; original: unknown };

let installed = false;
let targets: Target[] = [];
let pollTimer: ReturnType<typeof setInterval> | null = null;
const reported = new Set<string>();

function describe(fn: unknown): string {
  if (fn === Number.prototype.toString) return "Number.prototype.toString";
  if (fn === String.prototype.toString) return "String.prototype.toString";
  if (fn === Object.prototype.toString) return "Object.prototype.toString";
  try {
    return Function.prototype.toString.call(fn).slice(0, 300);
  } catch {
    return typeof fn;
  }
}

function report(target: Target, via: "atribuição" | "polling", replacement: unknown, stack?: string) {
  const key = `${target.name}:${via}`;
  if (reported.has(key)) return;
  reported.add(key);

  // console.error original: o Sonax intercepta o console, e não queremos que ele engula isso.
  const error =
    (window as Window & { __originalConsoleError?: typeof console.error }).__originalConsoleError ??
    console.error;
  error(`${TAG} ${target.name}.prototype.toString foi SOBRESCRITO`, {
    via,
    substituidoPor: describe(replacement),
    quando: new Date().toISOString(),
    rota: window.location.pathname + window.location.search,
    scriptsExternos: Array.from(document.scripts)
      .map((s) => s.src.split("?")[0])
      .filter((src) => src && !src.startsWith(window.location.origin)),
    userAgent: navigator.userAgent,
    stack: stack ?? "(indisponível — troca via defineProperty, detectada por polling)",
  });
}

export function installPrototypeIntegrityGuard(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;

  targets = [
    { name: "String", proto: String.prototype, original: String.prototype.toString },
    { name: "Number", proto: Number.prototype, original: Number.prototype.toString },
  ];

  for (const target of targets) {
    let current = target.original;
    try {
      // Accessor configurável e com setter: quem atribuir continua conseguindo (não
      // mudamos o comportamento), mas deixa o stack. `defineProperty` também segue
      // funcionando — esse caso o polling pega.
      Object.defineProperty(target.proto, "toString", {
        configurable: true,
        enumerable: false,
        get() {
          return current;
        },
        set(next) {
          current = next;
          if (next !== target.original) report(target, "atribuição", next, new Error().stack);
        },
      });
    } catch {
      // Protótipo já travado por alguém — fica só o polling.
    }
  }

  pollTimer = setInterval(() => {
    for (const target of targets) {
      const now = (target.proto as { toString: unknown }).toString;
      if (now !== target.original) report(target, "polling", now);
    }
  }, POLL_MS);
}

/** Só para testes: desfaz o accessor e para o polling. */
export function uninstallPrototypeIntegrityGuardForTests(): void {
  if (pollTimer) clearInterval(pollTimer);
  pollTimer = null;
  for (const target of targets) {
    Object.defineProperty(target.proto, "toString", {
      value: target.original,
      writable: true,
      configurable: true,
      enumerable: false,
    });
  }
  targets = [];
  installed = false;
  reported.clear();
}
